import React, { useEffect, useState, useCallback } from 'react';
import { 
  Typography, 
  Paper, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TextField, 
  Button, 
  Snackbar, 
  Alert, 
  MenuItem, 
  Select, 
  InputLabel, 
  FormControl, 
  Chip, 
  Stack, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  IconButton,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../api/axios';

interface TenantOption {
  id: number;
  name: string;
}

interface Platform {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  is_active: boolean;
}

interface PlatformCredential {
  id: number;
  platform_id: number;
  platform_name: string;
  tenant_id: number;
  tenant_name: string;
  credentials: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const PlatformCredentialsPage: React.FC = () => {
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [credentials, setCredentials] = useState<PlatformCredential[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<number | ''>('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Check if user is a tenant admin
  const userRole = localStorage.getItem('userRole');
  const userTenantId = localStorage.getItem('userTenantId');
  const isTenantAdmin = userRole === 'admin' && userTenantId;
  
  // Dialog states
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    credential: PlatformCredential | null;
    isNew: boolean;
  }>({ open: false, credential: null, isNew: false });
  
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/api/v1/admin/tenants/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTenants(res.data as TenantOption[]);
    } catch (err: any) {
      setError('Failed to fetch tenants');
    }
  };

  const fetchPlatforms = async () => {
    try {
      const token = localStorage.getItem('token');
      const userRole = localStorage.getItem('userRole');
      
      console.log('fetchPlatforms - userRole:', userRole);
      console.log('fetchPlatforms - api.defaults.baseURL:', api.defaults.baseURL);
      
      // Use different endpoints based on user role
      const endpoint = userRole === 'super_admin'
        ? '/api/v1/admin/platforms/all'
        : '/api/v1/tenant/platforms/';
        
      console.log('fetchPlatforms - endpoint:', endpoint);
      console.log('fetchPlatforms - full URL will be:', api.defaults.baseURL + endpoint);
        
      const res = await api.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      console.log('fetchPlatforms - response:', res.data);
      setPlatforms(res.data as Platform[]);
    } catch (err: any) {
      console.error('fetchPlatforms - error:', err);
      console.error('fetchPlatforms - error.response:', err.response);
      console.error('fetchPlatforms - error.config:', err.config);
      setError('Failed to fetch platforms');
    }
  };

  const fetchCredentials = useCallback(async () => {
    if (!selectedTenant) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const userRole = localStorage.getItem('userRole');
      
      // Debug logging
      console.log('fetchCredentials - userRole:', userRole);
      console.log('fetchCredentials - selectedTenant:', selectedTenant);
      
      // Use different endpoints based on user role
      const endpoint = userRole === 'super_admin' 
        ? `/api/v1/admin/tenants/${selectedTenant}/platforms/`
        : `/api/v1/tenant/platforms/credentials/`;
        
      console.log('fetchCredentials - endpoint:', endpoint);
        
      const res = await api.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Transform the data to include tenant and platform names
      const transformedCredentials = (res.data as any[]).map((cred: any) => ({
        ...cred,
        platform_name: platforms.find(p => p.id === cred.platform_id)?.display_name || 'Unknown',
        tenant_name: tenants.find(t => t.id === selectedTenant)?.name || 'Unknown'
      }));
      
      setCredentials(transformedCredentials);
    } catch (err: any) {
      setError('Failed to fetch platform credentials');
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, platforms, tenants]);

  useEffect(() => {
    if (isTenantAdmin) {
      // For tenant admins, automatically set their tenant
      setSelectedTenant(parseInt(userTenantId || '0'));
    } else {
      // For super admins, fetch tenants list
      fetchTenants();
    }
    fetchPlatforms();
  }, [isTenantAdmin, userTenantId]);

  useEffect(() => {
    if (selectedTenant && platforms.length > 0) {
      fetchCredentials();
    }
  }, [selectedTenant, platforms, fetchCredentials]);

  const handleCreateCredential = () => {
    setEditDialog({
      open: true,
      credential: null,
      isNew: true
    });
  };

  const handleEditCredential = (credential: PlatformCredential) => {
    setEditDialog({
      open: true,
      credential,
      isNew: false
    });
  };

  const handleCloseDialog = () => {
    setEditDialog({ open: false, credential: null, isNew: false });
  };

  const handleSaveCredential = async (formData: any) => {
    if (!selectedTenant) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const userRole = localStorage.getItem('userRole');
      
      if (editDialog.isNew) {
        // Create new credential - use different endpoints based on user role
        const endpoint = userRole === 'super_admin'
          ? `/api/v1/admin/tenants/${selectedTenant}/platforms/`
          : `/api/v1/tenant/platforms/${formData.platform_name}/configure`;
          
        const payload = userRole === 'super_admin'
          ? { platform_name: formData.platform_name, credentials: formData.credentials }
          : formData.credentials;
          
        await api.post(endpoint, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Platform credentials created successfully');
      } else {
        // Update existing credential - use different endpoints based on user role
        const endpoint = userRole === 'super_admin'
          ? `/api/v1/admin/tenants/${selectedTenant}/platforms/${formData.platform_id}/credentials`
          : `/api/v1/tenant/platforms/${formData.platform_name}/configure`;
          
        await api.post(endpoint, formData.credentials, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Platform credentials updated successfully');
      }
      
      fetchCredentials();
      handleCloseDialog();
    } catch (err: any) {
      setError('Failed to save platform credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (credential: PlatformCredential) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const userRole = localStorage.getItem('userRole');
      
      if (credential.is_active) {
        // Deactivate platform - use different endpoints based on user role
        const endpoint = userRole === 'super_admin'
          ? `/api/v1/admin/platforms/${credential.platform_id}/deactivate`
          : `/api/v1/tenant/platforms/${credential.platform_name}/deactivate`;
          
        const payload = userRole === 'super_admin'
          ? { tenant_id: credential.tenant_id }
          : {};
          
        await api.post(endpoint, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        // Activate platform - use different endpoints based on user role
        const endpoint = userRole === 'super_admin'
          ? `/api/v1/admin/platforms/${credential.platform_id}/activate`
          : `/api/v1/tenant/platforms/${credential.platform_name}/activate`;
          
        const payload = userRole === 'super_admin'
          ? { tenant_id: credential.tenant_id }
          : {};
          
        await api.post(endpoint, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      
      setSuccess(`Platform ${credential.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchCredentials();
    } catch (err: any) {
      setError('Failed to update platform status');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (key: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const renderCredentialValue = (key: string, value: any) => {
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    
    if (key.toLowerCase().includes('password') || key.toLowerCase().includes('secret')) {
      const isVisible = showPasswords[key] || false;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            value={isVisible ? value : '••••••••'}
            variant="outlined"
            size="small"
            InputProps={{ readOnly: true }}
            sx={{ flexGrow: 1 }}
          />
          <IconButton
            size="small"
            onClick={() => togglePasswordVisibility(key)}
          >
            {isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </Box>
      );
    }
    
    return value;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Platform Credentials Management
      </Typography>
      
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {isTenantAdmin ? (
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Typography variant="h6" color="primary">
                Managing credentials for your tenant
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <FormControl fullWidth>
                <InputLabel>Select Tenant</InputLabel>
                <Select
                  value={selectedTenant}
                  onChange={(e) => setSelectedTenant(e.target.value as number)}
                  label="Select Tenant"
                >
                  {tenants.map((tenant) => (
                    <MenuItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Box sx={{ flex: '1 1 auto' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateCredential}
              disabled={!selectedTenant}
              sx={{ mr: 1 }}
            >
              Add Platform Credentials
            </Button>
          </Box>
        </Box>
      </Paper>

      {selectedTenant && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Platform Credentials {isTenantAdmin ? 'for your tenant' : `for ${tenants.find(t => t.id === selectedTenant)?.name}`}
          </Typography>
          
          {loading ? (
            <Typography>Loading...</Typography>
          ) : credentials.length === 0 ? (
            <Typography color="text.secondary">
              No platform credentials configured for this tenant.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Platform</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Credentials</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {credentials.map((credential) => (
                    <TableRow key={`${credential.platform_id}-${credential.tenant_id}`}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">
                            {credential.platform_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {credential.platform_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={credential.is_active ? 'Active' : 'Inactive'}
                          color={credential.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 400 }}>
                          {Object.entries(credential.credentials || {}).map(([key, value]) => (
                            <Box key={key} sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {key}:
                              </Typography>
                              <Box sx={{ mt: 0.5 }}>
                                {renderCredentialValue(key, value)}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditCredential(credential)}
                            title="Edit Credentials"
                          >
                            <EditIcon />
                          </IconButton>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={credential.is_active}
                                onChange={() => handleToggleActive(credential)}
                                size="small"
                              />
                            }
                            label=""
                            labelPlacement="start"
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Edit/Create Dialog */}
      <Dialog 
        open={editDialog.open} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {editDialog.isNew ? 'Add Platform Credentials' : 'Edit Platform Credentials'}
        </DialogTitle>
        <DialogContent>
          <PlatformCredentialForm
            credential={editDialog.credential}
            platforms={platforms}
            tenantId={selectedTenant}
            onSave={handleSaveCredential}
            onCancel={handleCloseDialog}
            isNew={editDialog.isNew}
          />
        </DialogContent>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')}>
        <Alert severity="success">{success}</Alert>
      </Snackbar>
    </Box>
  );
};

// Platform Credential Form Component
interface PlatformCredentialFormProps {
  credential: PlatformCredential | null;
  platforms: Platform[];
  tenantId: number | '';
  onSave: (data: any) => void;
  onCancel: () => void;
  isNew: boolean;
}

const PlatformCredentialForm: React.FC<PlatformCredentialFormProps> = ({
  credential,
  platforms,
  tenantId,
  onSave,
  onCancel,
  isNew
}) => {
  // When editing, find the platform by ID to get the correct platform name
  const editingPlatform = credential && !isNew
    ? platforms.find(p => p.id === credential.platform_id)
    : null;

  const [formData, setFormData] = useState({
    platform_name: editingPlatform?.name || credential?.platform_name || '',
    platform_id: credential?.platform_id || '',
    credentials: credential?.credentials || {}
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tenantId) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const userRole = localStorage.getItem('userRole');
      
      // Use different endpoints based on user role
      const endpoint = userRole === 'super_admin' 
        ? `/api/v1/admin/tenants/${tenantId}/platforms/${formData.platform_name}/configure`
        : `/api/v1/tenant/platforms/${formData.platform_name}/configure`;
        
      const response = await api.post(endpoint, formData.credentials, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if ((response.data as any).success) {
        onCancel();
      }
    } catch (err: any) {
      console.error('Error saving credentials:', err);
    }
  };

  const updateCredential = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      credentials: {
        ...prev.credentials,
        [key]: value
      }
    }));
  };

  const getPlatformFields = (platformName: string) => {
    // API Integration platforms (same dev team, same format)
    const apiIntegrationPlatforms = ['vblink', 'ultrapanda', 'legendfire', 'glamourspin', 'goldentreasure', 'egame'];
    const apiIntegrationDisplayNames = ['vblink', 'ultrapanda', 'legendfire', 'glamour spin', 'golden treasure', 'egame'];

    // POS platforms (Golden Dragon, Magic City) need username, password, and moneybox
    const posPlatforms = ['golden_dragon', 'magic_city'];
    const posDisplayNames = ['golden dragon', 'magic city'];

    const normalizedName = platformName.toLowerCase().replace(/\s+/g, '');

    // Check if it's a POS platform
    const isPosPlatform = posPlatforms.includes(platformName.toLowerCase()) ||
                         posDisplayNames.includes(platformName.toLowerCase());

    if (isPosPlatform) {
      // Golden Dragon needs kiosk_id, Magic City doesn't
      if (platformName.toLowerCase().includes('golden')) {
        return ['username', 'password', 'kiosk_id', 'moneybox', 'base_url'];
      } else {
        return ['username', 'password', 'moneybox', 'base_url'];
      }
    }

    // Check if it's an API Integration platform
    const isApiIntegration = apiIntegrationPlatforms.includes(normalizedName) ||
                            apiIntegrationDisplayNames.includes(platformName.toLowerCase()) ||
                            platformName.toLowerCase().includes('vblink') ||
                            platformName.toLowerCase().includes('ultrapanda') ||
                            platformName.toLowerCase().includes('legendfire') ||
                            platformName.toLowerCase().includes('glamour') ||
                            platformName.toLowerCase().includes('golden treasure') ||
                            platformName.toLowerCase().includes('egame');

    if (isApiIntegration) {
      return ['base_url', 'app_id', 'app_secret', 'agent_account', 'agent_password'];
    } else {
      // Simple platforms need 2 parameters
      return ['username', 'password'];
    }
  };

  const getPlatformType = (platformName: string) => {
    const apiIntegrationPlatforms = ['vblink', 'ultrapanda', 'legendfire', 'glamourspin', 'goldentreasure', 'egame'];
    const apiIntegrationDisplayNames = ['vblink', 'ultrapanda', 'legendfire', 'glamour spin', 'golden treasure', 'egame'];
    const posPlatforms = ['golden_dragon', 'magic_city'];
    const posDisplayNames = ['golden dragon', 'magic city'];

    const normalizedName = platformName.toLowerCase().replace(/\s+/g, '');

    // Check if it's a POS platform
    const isPosPlatform = posPlatforms.includes(platformName.toLowerCase()) ||
                         posDisplayNames.includes(platformName.toLowerCase());
    if (isPosPlatform) return 'pos';

    // Check if it's an API Integration platform
    const isApiIntegration = apiIntegrationPlatforms.includes(normalizedName) ||
                            apiIntegrationDisplayNames.includes(platformName.toLowerCase()) ||
                            platformName.toLowerCase().includes('vblink') ||
                            platformName.toLowerCase().includes('ultrapanda') ||
                            platformName.toLowerCase().includes('legendfire') ||
                            platformName.toLowerCase().includes('glamour') ||
                            platformName.toLowerCase().includes('golden treasure') ||
                            platformName.toLowerCase().includes('egame');

    return isApiIntegration ? 'api' : 'simple';
  };

  const selectedPlatform = platforms.find(p => p.name === formData.platform_name);
  const requiredFields = selectedPlatform ? getPlatformFields(selectedPlatform.name) : [];
  const platformType = selectedPlatform ? getPlatformType(selectedPlatform.name) : 'simple';

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ pt: 2 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Platform</InputLabel>
          <Select
            value={formData.platform_name}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              platform_name: e.target.value,
              platform_id: platforms.find(p => p.name === e.target.value)?.id || '',
              credentials: {} // Reset credentials when platform changes
            }))}
            label="Platform"
            disabled={!isNew}
          >
            {platforms.map((platform) => (
              <MenuItem key={platform.id} value={platform.name}>
                {platform.display_name} ({platform.name})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedPlatform && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2, p: 1, bgcolor: platformType === 'api' ? 'primary.light' : platformType === 'pos' ? 'warning.light' : 'secondary.light', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Platform Type: <strong>
                  {platformType === 'api' ? 'API Integration' : platformType === 'pos' ? 'POS System' : 'Simple Authentication'}
                </strong>
                {platformType === 'api' && ' (5 parameters required)'}
                {platformType === 'pos' && selectedPlatform.name === 'golden_dragon' && ' (5 parameters required)'}
                {platformType === 'pos' && selectedPlatform.name === 'magic_city' && ' (4 parameters required)'}
                {platformType === 'simple' && ' (2 parameters required)'}
              </Typography>
            </Box>
            <Typography variant="h6" gutterBottom>
              Platform Credentials
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure the credentials for {selectedPlatform.display_name}
            </Typography>
            
            {requiredFields.map((field) => (
              <TextField
                key={field}
                fullWidth
                label={field.replace('_', ' ').toUpperCase()}
                value={formData.credentials[field] || ''}
                onChange={(e) => updateCredential(field, e.target.value)}
                type={field.toLowerCase().includes('password') || field.toLowerCase().includes('secret') ? 'password' : 'text'}
                margin="normal"
                required
                helperText={
                  field === 'base_url' ? 'Base URL for the platform API or POS system' :
                  field === 'app_id' ? 'Application ID provided by the platform' :
                  field === 'app_secret' ? 'Application secret key' :
                  field === 'agent_account' ? 'Agent account username' :
                  field === 'agent_password' ? 'Agent account password' :
                  field === 'kiosk_id' ? 'Kiosk ID for Golden Dragon POS' :
                  field === 'moneybox' ? 'Money box/Cash drawer ID (default: 1)' :
                  field === 'username' ? 'POS system username (Boss login) or platform username' :
                  field === 'password' ? 'POS system password (Boss login) or platform password' : ''
                }
              />
            ))}
          </>
        )}
      </Box>
      
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button 
          type="submit" 
          variant="contained"
          disabled={!formData.platform_name || requiredFields.some(field => !formData.credentials[field])}
        >
          {isNew ? 'Create' : 'Update'}
        </Button>
      </DialogActions>
    </form>
  );
};

export default PlatformCredentialsPage;
